import React from 'react';
import { Link } from 'react-router-dom';

const ProjectCard = ({ project }) => {
  // Calcul du pourcentage de financement
  const fundingPercentage = Math.min(Math.round((project.raised / project.goal) * 100), 100);
  
  // Calcul du temps restant et vérifie si la campagne est terminée
  const calculateTimeLeft = () => {
    const endDate = new Date(project.endDate);
    const now = new Date();
    const diffTime = endDate - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays <= 0) return "Campagne terminée";
    if (diffDays === 1) return "Dernier jour";
    return `${diffDays} jours restants`;
  };
  
  // Vérifie si la campagne est terminée
  const isCampaignEnded = () => {
    const endDate = new Date(project.endDate);
    const now = new Date();
    return now > endDate;
  };
  
  const campaignEnded = isCampaignEnded();
  
  // Texte alternatif enrichi pour l'image
  const imageAltText = `Affiche du projet "${project.title}" réalisé par ${project.director}`;
  
  return (
    <div className={`bg-white rounded-lg overflow-hidden shadow-lg border border-gray-100 ${campaignEnded ? 'opacity-80' : ''}`}>
      <div className="relative">
        <Link to={`/projets/${project.id}`}>
          <img 
            src={project.poster || "/images/placeholder-project.jpg"} 
            alt={imageAltText}
            className="w-full h-64 object-cover"
          />
        </Link>
        <div className={`absolute bottom-0 left-0 right-0 ${campaignEnded ? 'bg-gradient-to-t from-gray-800/80 to-transparent' : 'bg-gradient-to-t from-black/80 to-transparent'} p-4`}>
          <p className={`text-white text-sm font-medium ${campaignEnded ? 'bg-gray-700' : ''} px-2 py-1 rounded inline-block`}>
            {calculateTimeLeft()}
          </p>
        </div>
      </div>
      
      <div className="p-6">
        <div className="flex justify-between items-start mb-2">
          <Link to={`/projets/${project.id}`}>
            <h3 className="font-bold text-xl leading-tight hover:text-amber-600 transition">{project.title}</h3>
          </Link>
          <span className="bg-gray-100 text-xs px-2 py-1 rounded">{project.genre}</span>
        </div>
        
        <p className="text-gray-600 text-sm">Réalisé par {project.director}</p>
        
        <p className="mt-3 text-gray-700 line-clamp-3">{project.synopsis}</p>
        
        <div className="mt-4">
          <div className="flex justify-between text-sm mb-1">
            <span className="font-medium">{fundingPercentage}% financé</span>
            <span>{project.raised.toLocaleString()} FCFA sur {project.goal.toLocaleString()} FCFA</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2.5">
            <div 
              className={`${campaignEnded ? 'bg-gray-500' : 'bg-amber-500'} h-2.5 rounded-full`} 
              style={{ width: `${fundingPercentage}%` }}
              aria-valuenow={fundingPercentage}
              aria-valuemin="0"
              aria-valuemax="100"
              role="progressbar"
              aria-label={`${fundingPercentage}% financé`}
            ></div>
          </div>
        </div>
        
        <div className="mt-6 flex space-x-3">
          <Link 
            to={`/projets/${project.id}`} 
            className={`${campaignEnded ? 'bg-gray-500 hover:bg-gray-600' : 'bg-amber-500 hover:bg-amber-600'} text-white px-4 py-2 rounded-full text-sm font-medium transition flex-1 text-center`}
            aria-label={`Contribuer au projet ${project.title}`}
          >
            {campaignEnded ? 'Voir le projet' : 'Contribuer'}
          </Link>
          {project.associatedFilm && (
            <Link 
              to={`/films/${project.associatedFilm}`} 
              className="border border-gray-300 hover:border-gray-400 text-gray-700 px-4 py-2 rounded-full text-sm font-medium transition text-center"
              aria-label={`Voir le film associé au projet ${project.title}`}
            >
              Film associé
            </Link>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProjectCard;
